import React, { useLayoutEffect, useRef, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSAlert } from '../../components/ILMSAlert';
import { getApiErrorMessage } from '../../services/api';
import { today } from '../../utils/format';
import { cellValue, downloadXlsx } from '../../utils/xlsx';

export interface ExportColumn<T> {
  key: string;
  header: string;
  render: (row: T, rowIndex: number) => React.ReactNode;
}

interface ExportExcelProps<T> {
  /** File and sheet name, e.g. "Milk Sessions". */
  title: string;
  columns: ExportColumn<T>[];
  /** Every record (the date range is applied here, not by the caller). */
  fetchRows: () => Promise<T[]>;
  /** The date a row belongs to (YYYY-MM-DD…) - without it there is no date range. */
  dateOf?: (row: T) => string | null | undefined;
}

const shiftDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** "Excel" button: pick a date range, get an .xlsx of exactly what the table
 * shows (same columns, same text) for every record in that range. */
export function ExportExcel<T>({ title, columns, fetchRows, dateOf }: ExportExcelProps<T>) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => `${today().slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // The rows to write: rendered off-screen with the table's own cell
  // renderers (animal names, statuses... exactly as on screen), then read
  // back as text once React has drawn them.
  const [pending, setPending] = useState<T[] | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const exportCols = columns.filter((c) => c.header);

  useLayoutEffect(() => {
    if (!pending || !tableRef.current) return;
    const body = Array.from(tableRef.current.tBodies[0]?.rows ?? []).map((tr) => Array.from(tr.cells).map((td) => {
      const cell = td.cloneNode(true) as HTMLElement;
      // Approve/Reject buttons, photo links and the like aren't data.
      cell.querySelectorAll('button, img, svg, [data-export-skip]').forEach((n) => n.remove());
      return cellValue(cell.textContent ?? '');
    }));
    const range = !dateOf ? today() : from || to ? `${from || 'start'}_to_${to || today()}` : `all_${today()}`;
    downloadXlsx(`${title} ${range}`.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_'), title, exportCols.map((c) => c.header), body);
    setPending(null);
    setBusy(false);
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const run = async () => {
    setError(null);
    setNote(null);
    if (dateOf && from && to && from > to) {
      setError('The From date is after the To date.');
      return;
    }
    setBusy(true);
    try {
      const all = await fetchRows();
      const rows = dateOf
        ? all.filter((r) => {
          const d = (dateOf(r) ?? '').slice(0, 10);
          return d !== '' && (!from || d >= from) && (!to || d <= to);
        })
        : all;
      if (rows.length === 0) {
        setNote('Nothing in this date range.');
        setBusy(false);
        return;
      }
      setPending(rows);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load the records to export.'));
      setBusy(false);
    }
  };

  const preset = (label: string, f: string, t: string) => (
    <button
      type="button"
      onClick={() => { setFrom(f); setTo(t); }}
      className={`rounded-full border px-3 py-1 text-helper-text ${from === f && to === t ? 'border-brand-green-600 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/30 text-ink-600 hover:bg-surface-subtle'}`}
    >
      {label}
    </button>
  );
  const t = today();

  return (
    <>
      <ILMSButton variant="outline" icon={<FileSpreadsheet />} onClick={() => { setOpen(true); setError(null); setNote(null); }}>
        Excel
      </ILMSButton>
      <ILMSModal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title={`Download ${title} (Excel)`}
        size="sm"
        footer={(
          <div className="flex w-full justify-end gap-2">
            <ILMSButton variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</ILMSButton>
            <ILMSButton icon={<FileSpreadsheet />} loading={busy} onClick={run}>Download</ILMSButton>
          </div>
        )}
      >
        <div className="flex flex-col gap-4">
          {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}
          {note && <ILMSAlert tone="info">{note}</ILMSAlert>}
          {dateOf ? (
            <>
              <div className="flex flex-wrap gap-2">
                {preset('Today', t, t)}
                {preset('Last 7 days', shiftDays(t, -6), t)}
                {preset('This month', `${t.slice(0, 8)}01`, t)}
                {preset('Last 30 days', shiftDays(t, -29), t)}
                {preset('All', '', '')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ILMSDatePicker label="From" value={from} onChange={(e) => setFrom(e.target.value)} />
                <ILMSDatePicker label="To" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <p className="text-helper-text text-ink-500">Leave a date blank for no limit on that side.</p>
            </>
          ) : (
            <p className="text-secondary text-ink-600">Downloads every record in this list.</p>
          )}
        </div>
      </ILMSModal>
      {pending && (
        <div aria-hidden="true" style={{ position: 'fixed', left: -10000, top: 0, width: 1, height: 1, overflow: 'hidden' }}>
          <table ref={tableRef}>
            <tbody>
              {pending.map((row, i) => (
                <tr key={i}>{exportCols.map((c) => <td key={c.key}>{c.render(row, i)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
